import { formatDamageFormula, parseDamageFormula } from "../calculations/damageFormula";
import { normalizeDamageType } from "../catalogs/damageTypes";

export const DEFAULT_CURRENCIES = [
  { currency: "Platinum Pieces", abbreviation: "PP" },
  { currency: "Gold Pieces", abbreviation: "GP" },
  { currency: "Silver Pieces", abbreviation: "SP" },
  { currency: "Copper Pieces", abbreviation: "CP" },
];

class Inventory {
  constructor(json) {
    const suppliedMoney = json.money ?? [];
    this.maxWeight = json.maxWeight;
    this.money = DEFAULT_CURRENCIES.map((defaultCurrency) => new Money(
      suppliedMoney.find((entry) => entry.currency === defaultCurrency.currency) ?? { ...defaultCurrency, amount: 0 }
    ));
    this.items = (json.items ?? []).map((itemJson) => createItem(itemJson));
  }
}

class Money {
  constructor(json) {
    this.amount = Number(json.amount) || 0;
    this.currency = json.currency;
  }
}

class BaseItem {
  constructor(json) {
    this.id = json.id;
    this.catalogId = json.catalogId || "";
    this.name = json.name;
    this.type = String(json.type || "").toLowerCase();
    this.weight = json.weight;
    this.price = json.price;
    this.notes = json.notes;
    this.sourceUrl = json.sourceUrl || "";
    const amount = Number(json.amount ?? 1);
    this.amount =
      Number.isFinite(amount) && amount >= 0 ? Math.floor(amount) : 1;
  }
}

class Weapon extends BaseItem {
  constructor(json) {
    super(json);
    const weaponType = String(json.weaponType || "melee").toLowerCase();
    const parsedDamage = parseDamageFormula(json.damage);
    const parsedCritical = parseDamageFormula(json.crit);

    this.isActive = Boolean(json.isActive);
    this.modifier = Number(json.modifier) || 0;
    this.range = json.range || "";
    this.damageDiceCount = Number(
      json.damageDiceCount ?? parsedDamage.diceCount
    ) || 0;
    this.damageDiceSides = Number(
      json.damageDiceSides ?? parsedDamage.diceSides
    ) || 0;
    this.damageBonus = Number(json.damageBonus ?? parsedDamage.bonus) || 0;
    this.damage = formatDamageFormula({
      diceCount: this.damageDiceCount,
      diceSides: this.damageDiceSides,
      bonus: this.damageBonus,
    }) || json.damage || "";
    this.critDiceCount = Number(
      json.critDiceCount ?? parsedCritical.diceCount
    ) || 0;
    this.critDiceSides = Number(
      json.critDiceSides ?? parsedCritical.diceSides
    ) || 0;
    this.critBonus = Number(json.critBonus ?? parsedCritical.bonus) || 0;
    this.crit = formatDamageFormula({
      diceCount: this.critDiceCount,
      diceSides: this.critDiceSides,
      bonus: this.critBonus,
    }) || json.crit || "";
    this.weaponType = ["ranged", "long-range", "longrange"].includes(weaponType)
      ? "ranged"
      : "melee";
    this.ammunitionId = this.weaponType === "ranged" ? json.ammunitionId || "" : "";

    this.damageType = normalizeDamageType(json.damageType);
  }
}
class Ammunition extends BaseItem {
  constructor(json) {
    super(json);
    const parsedDamage = parseDamageFormula(json.damage);

    this.damageDiceCount = Number(
      json.damageDiceCount ?? parsedDamage.diceCount
    ) || 0;
    this.damageDiceSides = Number(
      json.damageDiceSides ?? parsedDamage.diceSides
    ) || 0;
    this.damageBonus = Number(json.damageBonus ?? parsedDamage.bonus) || 0;
    this.damage = formatDamageFormula({
      diceCount: this.damageDiceCount,
      diceSides: this.damageDiceSides,
      bonus: this.damageBonus,
    }) || json.damage || "";
    this.damageType = normalizeDamageType(json.damageType);
  }
}

class Armor extends BaseItem {
  constructor(json) {
    super(json);
    this.isActive = Boolean(json.isActive);
    this.armorClass = Number(json.armorClass) || 0;
    this.armorType = String(json.armorType || "armor").toLowerCase();
  }
}
class MagicItem extends BaseItem {
  constructor(json) {
    super(json);
    this.isSpellcastingActive = Boolean(json.isSpellcastingActive);
  }
}

class ConsumableItem extends BaseItem {}
class GearItem extends BaseItem {}

const itemClasses = {
  weapon: Weapon,
  ammunition: Ammunition,
  armor: Armor,
  magic: MagicItem,
  consumable: ConsumableItem,
  gear: GearItem,
};

export function createItem(json) {
  const itemClass = itemClasses[json.type.toLowerCase()];
  if (!itemClass) throw new Error("Unsupported item type");
  return new itemClass(json);
}

export default Inventory;
